// ============================================================================
// CÓMO COTIZA TU BOT — las reglas de precio, en pantalla (T-PRE-12, R-PRE-01).
//
// ⛔⛔ ESTA PANTALLA TOCA PLATA. Lo que se edita aquí son los descuentos con los
// que el bot le dice un precio a un cliente real de Living House por WhatsApp.
// Una regresión aquí no rompe una pantalla: hace que el bot dé un precio que no
// es, o que deje de cotizar en mitad de una venta.
//
// ⛔⛔ LA PREVISUALIZACIÓN LLAMA A `cotizar` DEL MOTOR, IMPORTADO. No hay ni una
// fórmula de precio escrita en este archivo, y eso NO es comodidad:
//   · `cotizar` redondea a miles. Un `base * (1 - pct/100)` en el navegador da
//     $2.141.600 donde el bot dice $2.142.000 — la pantalla enseñaría un precio
//     y el bot diría otro, y nadie sabría cuál es el bueno.
//   · `cotizar` es quien conoce el COLAPSO POR REDONDEO. Reimplementarlo aquí
//     sería copiar el invariante más delicado del sistema a un segundo sitio,
//     y el del navegador se lo salta cualquiera. 📜 Es la lección de T-CON-9.
// Lo mide **V-101** (el precio previsualizado es el redondeado, no el crudo) y
// **V-102** (el colapso se ve ANTES de guardar).
//
// ⛔ LA BARANDA DE VERDAD NO ESTÁ AQUÍ. Config sin medios, `desc_pct` fuera de
// `[0,100)` y nombres de medio repetidos los rechaza el trigger
// `reglas_precio_guard` (migración `0053`), que es donde no se lo salta nadie —
// ni esta pantalla, ni quien escriba por PostgREST con un token de Victorix.
// Aquí solo se TRADUCE su rechazo (V-105): el crudo `VX_REGLAS_INVALIDAS` en la
// cara del asesor es justo el patrón que R-PRE-01 prohíbe.
//
// ⚠️ EL COLAPSO POR REDONDEO NO LO PUEDE VALIDAR LA BASE, y no se finge que sí:
// depende del precio de cada producto, y el catálogo no vive en esta base (lo
// sirve un adapter externo). Por eso este es el único sitio donde se ve antes
// de guardar — y por eso el barrido de abajo existe.
//
// ⚠️ EL BARRIDO ES UN BARRIDO, NO UNA DEMOSTRACIÓN. Se probó buscar «el precio
// mínimo que el bot puede cotizar» por bisección y **da un número falso**: con
// las reglas de Living House (20/15/10 + miles) colapsan $1.000…$12.000 y
// $16.000 y $17.000, pero $13.000, $14.000 y $15.000 NO. El colapso no es
// monótono en el precio (medido el 1-sep-2026 con el propio `cotizar`), así que
// lo que se enseña es «de estos N precios probados, estos no se pueden cotizar»
// — un hecho, no una garantía.
//
// ⛔ EL DUEÑO MIRA Y NO TOCA (R-PRE-01, mismo molde que `/guion`). Es plata: que
// el dueño VEA sus descuentos es media garantía; que pueda subírselos es cómo
// el bot acaba cotizando a pérdida. Lo mide **V-100**.
// ============================================================================
import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Wallet } from "lucide-react";
import { Aviso, Boton, SkeletonLista } from "./piezas.tsx";
import { explicarRechazoDeReglas, guardarReglasPrecio, leerReglasPrecio } from "./datos.ts";
import type { FilaReglasPrecio } from "./datos.ts";
// ⛔⛔ LA MISMA FUNCIÓN QUE USA EL BOT. No se copia, no se reescribe, no se
// «adapta»: se importa del worker. Si algún día no se pudiera importar, la
// respuesta NO es copiarla — es parar y decirlo.
import { cotizar, formatearCOP } from "../../../worker/src/tools/cotizar.ts";
import type { MedioPago, ReglasPrecio } from "../../../worker/src/tools/cotizar.ts";
import type { ContextoPanel } from "./tipos.ts";

/** Cuánto tarda el worker en releer el entrenamiento —guion, conocimiento Y
 *  reglas de precio, en el MISMO tic— (`worker/src/index.ts:824-837`). */
const MINUTOS_HASTA_QUE_EL_BOT_LO_TOMA = 15;

/** Precio de ejemplo de arranque. Del orden de un mueble real de Living House,
 *  y con decimales que el redondeo a miles SÍ mueve: con un precio redondo la
 *  previsualización no distinguiría `cotizar` de una regla de tres. */
const PRECIO_EJEMPLO_DEFECTO = 2_677_000;

/** El barrido: precios redondos de $1.000 a $100.000. Ahí es donde vive el
 *  colapso con reglas realistas; por encima, la diferencia entre descuentos es
 *  mucho mayor que el paso del redondeo. */
const BARRIDO_DESDE = 1_000;
const BARRIDO_HASTA = 100_000;
const BARRIDO_PASO = 1_000;

/** Redondeos que entiende `cotizar`. Se listan aquí para que la pantalla no
 *  pueda ofrecer uno que el motor no conozca. */
const REDONDEOS: { valor: "miles" | "ninguno"; etiqueta: string }[] = [
  { valor: "miles", etiqueta: "A miles ($2.142.000)" },
  { valor: "ninguno", etiqueta: "Al peso ($2.141.600)" },
];

// ---------------------------------------------------------------------------

/** Lo que `cotizar` dice de UNA config, ya en cristiano. */
type Previsualizacion =
  | { ok: true; medios: { medio: string; desc_pct: number; precio: number }[] }
  | { ok: false; colapsa: boolean; motivo: string };

/**
 * ⛔ TODO LO QUE ESTA FUNCIÓN SABE DE PRECIOS SE LO PREGUNTA A `cotizar`. Aquí
 * solo se traduce a español lo que el motor decide. Si esta función calculara,
 * el sistema tendría dos verdades sobre un precio.
 */
export function previsualizar(precioBase: number, reglas: ReglasPrecio): Previsualizacion {
  try {
    const c = cotizar(precioBase, reglas);
    return { ok: true, medios: c.medios.map((m) => ({ medio: m.medio, desc_pct: m.desc_pct, precio: m.precio })) };
  } catch (e: any) {
    const msg = typeof e?.message === "string" ? e.message : "";
    if (msg.includes("REGLAS_PRECIO_COLAPSAN")) {
      return {
        ok: false, colapsa: true,
        motivo:
          "Con estos descuentos y este redondeo, dos medios de pago le quedarían al mismo precio " +
          "(o el más barato saldría más caro). El bot NO cotiza cuando eso pasa: escala la " +
          "conversación a un asesor, en mitad de la venta.",
      };
    }
    if (msg.startsWith("descuento inválido")) {
      return { ok: false, colapsa: false, motivo: "Hay un descuento fuera de rango: tiene que estar entre 0 y 99,99." };
    }
    if (msg.includes("sin medios de pago")) {
      return { ok: false, colapsa: false, motivo: "Sin medios de pago el bot no puede cotizar nada." };
    }
    if (msg.startsWith("precio base inválido")) {
      return { ok: false, colapsa: false, motivo: "Escribe un precio de ejemplo mayor que cero." };
    }
    return { ok: false, colapsa: false, motivo: "No se pudo calcular la cotización con esta configuración." };
  }
}

/** Los precios del barrido que el bot NO podría cotizar con esta config. */
export function barridoQueColapsa(reglas: ReglasPrecio): number[] {
  const malos: number[] = [];
  for (let p = BARRIDO_DESDE; p <= BARRIDO_HASTA; p += BARRIDO_PASO) {
    const r = previsualizar(p, reglas);
    if (!r.ok && r.colapsa) malos.push(p);
  }
  return malos;
}

function cuantosPrecios(): number {
  return Math.floor((BARRIDO_HASTA - BARRIDO_DESDE) / BARRIDO_PASO) + 1;
}

// ---------------------------------------------------------------------------

export function Precios({ ctx }: { ctx: ContextoPanel }) {
  const { sb, tenantId, rol } = ctx;
  // `esDueno` se usa para EXPLICAR; `puedeEditar` es lo que abre la escritura.
  const esDueno = rol === "dueno";
  const puedeEditar = rol === "victorix";

  const [fila, setFila] = useState<FilaReglasPrecio | null>(null);
  const [borrador, setBorrador] = useState<ReglasPrecio | null>(null);
  const [precioEjemplo, setPrecioEjemplo] = useState(PRECIO_EJEMPLO_DEFECTO);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [refresco, setRefresco] = useState(0);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const f = await leerReglasPrecio(sb, tenantId);
        if (!vivo) return;
        setFila(f);
        setBorrador(f ? clonar(f.config) : { medios_pago: [], redondeo: "miles" });
        setError(null);
      } catch (e) {
        if (!vivo) return;
        setError(explicarRechazoDeReglas(e));
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, [sb, tenantId, refresco]);

  // Lo que se previsualiza es **lo que se va a guardar** (el borrador), no lo
  // que hay guardado: previsualizar lo viejo no avisaría de nada.
  const reglas: ReglasPrecio | null = puedeEditar ? borrador : (fila ? fila.config : null);

  const vista = useMemo(
    () => (reglas ? previsualizar(precioEjemplo, reglas) : null),
    [reglas, precioEjemplo],
  );
  const colapsos = useMemo(() => (reglas ? barridoQueColapsa(reglas) : []), [reglas]);

  function cambiarMedio(i: number, cambio: Partial<MedioPago>) {
    setBorrador((b) => b && ({
      ...b,
      medios_pago: b.medios_pago.map((m, j) => (j === i ? { ...m, ...cambio } : m)),
    }));
  }

  function quitarMedio(i: number) {
    setBorrador((b) => b && ({ ...b, medios_pago: b.medios_pago.filter((_, j) => j !== i) }));
  }

  function agregarMedio() {
    setBorrador((b) => b && ({ ...b, medios_pago: [...b.medios_pago, { nombre: "", desc_pct: 0 }] }));
  }

  async function alGuardar() {
    if (!borrador || ocupado) return;
    setOcupado(true); setError(null); setAviso(null);
    try {
      const guardada = await guardarReglasPrecio(sb, tenantId, borrador);
      setFila(guardada);
      setAviso(
        `Guardado. El bot cotiza con estas reglas en hasta ${MINUTOS_HASTA_QUE_EL_BOT_LO_TOMA} minutos.`,
      );
      setRefresco((n) => n + 1);
    } catch (e) {
      // ⛔ Traducido SIEMPRE (V-105). El marcador crudo de la base en la cara del
      // asesor no le dice nada y le esconde qué tiene que arreglar.
      setError(explicarRechazoDeReglas(e));
    } finally {
      setOcupado(false);
    }
  }

  const hayCambios = !!(borrador && fila && JSON.stringify(borrador) !== JSON.stringify(fila.config));
  const hayCambiosSinFila = !!(borrador && !fila && borrador.medios_pago.length > 0);

  return (
    <div className="h-full flex flex-col min-w-0">
      <header className="shrink-0 px-4 sm:px-6 pt-4 pb-3 border-b border-borde-suave">
        <h1 className="text-[19px] font-semibold tracking-tight">Cómo cotiza tu bot</h1>
        <p className="text-[12.5px] text-tinta-2 mt-0.5">
          Los medios de pago, su descuento y cómo se redondea el precio.
        </p>
      </header>

      <div className="flex-1 overflow-y-auto scroll-fino px-4 sm:px-6 py-4">
        <div className="max-w-[680px] mx-auto w-full space-y-3">
          {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}
          {aviso && <Aviso tono="ok" onCerrar={() => setAviso(null)}>{aviso}</Aviso>}

          {/* R-PRE-01 · se dice quién lo mantiene, ANTES de que lo busque. */}
          <Aviso tono="alerta">
            {esDueno
              ? <>Estos son los descuentos con los que tu bot cotiza. Los mantiene Victorix y no se
                  editan desde aquí: un porcentaje mal puesto hace que el bot venda a pérdida o que
                  deje de dar precios. Si quieres cambiar alguno, pídeselo a tu contacto de Victorix
                  y lo verás aquí mismo.</>
              : puedeEditar
                ? <>Antes de guardar, mira abajo lo que el bot va a cotizar. El bot toma las reglas
                    nuevas en hasta {MINUTOS_HASTA_QUE_EL_BOT_LO_TOMA} minutos.</>
                : <>Esta pantalla es para mirar. Las reglas de precio las escribe Victorix.</>}
          </Aviso>

          {cargando && <SkeletonLista filas={3} />}

          {!cargando && !fila && !puedeEditar && (
            <div data-sin-reglas className="border border-borde rounded-xl bg-superficie p-5 text-center">
              <p className="text-[14px] font-semibold">Este negocio todavía no tiene reglas de precio</p>
              <p className="text-[12.5px] text-tinta-2 mt-1.5 leading-snug">
                Mientras no las tenga, el bot no da precios: prefiere pasar la conversación a una
                persona antes que inventar uno. Las escribe Victorix cuando se pone en marcha el negocio.
              </p>
            </div>
          )}

          {!cargando && reglas && (<>
            <section data-medios className="border border-borde rounded-xl bg-superficie p-4">
              <h2 className="text-[14px] font-semibold flex items-center gap-2">
                <span className="text-tinta-2"><Wallet size={15} /></span>
                Medios de pago
              </h2>
              <p className="text-[12.5px] text-tinta-2 mt-1 leading-snug">
                El descuento se aplica sobre el precio de lista. A más descuento, más barato.
              </p>

              {reglas.medios_pago.length === 0 && (
                <p data-sin-medios className="text-[12.5px] text-tinta-2 mt-2.5 italic">
                  Todavía no hay ningún medio de pago. Sin al menos uno, el bot no puede cotizar.
                </p>
              )}

              <ul className="mt-2.5 divide-y divide-borde-suave">
                {reglas.medios_pago.map((m, i) => (
                  <li key={i} data-medio={i} className="flex items-center gap-3 py-2">
                    {puedeEditar ? (<>
                      <input
                        aria-label={`Nombre del medio ${i + 1}`}
                        value={m.nombre}
                        onChange={(e) => cambiarMedio(i, { nombre: e.target.value })}
                        placeholder="Efectivo"
                        className="flex-1 min-w-0 h-9 px-2.5 text-[13px] bg-elevado border border-borde
                                   rounded-lg focus:outline-none focus:border-marca/50"
                      />
                      <input
                        aria-label={`Descuento de ${m.nombre || `el medio ${i + 1}`}`}
                        type="number" step="0.01" min={0} max={99.99}
                        value={m.desc_pct}
                        onChange={(e) => cambiarMedio(i, { desc_pct: Number(e.target.value) })}
                        className="w-24 h-9 px-2.5 text-[13px] bg-elevado border border-borde
                                   rounded-lg focus:outline-none focus:border-marca/50"
                      />
                      <span className="text-[13px] text-tinta-2">%</span>
                      <Boton tam="sm" tono="neutro" disabled={ocupado}
                        onClick={() => quitarMedio(i)}>
                        <Trash2 size={14} />
                        <span className="sr-only">Quitar {m.nombre || `medio ${i + 1}`}</span>
                      </Boton>
                    </>) : (<>
                      <span data-nombre className="flex-1 min-w-0 text-[13px] font-medium">{m.nombre}</span>
                      <span data-descuento className="text-[13px] text-tinta-2">{m.desc_pct}% de descuento</span>
                    </>)}
                  </li>
                ))}
              </ul>

              {puedeEditar && (
                <Boton tam="sm" tono="neutro" onClick={agregarMedio} disabled={ocupado}>
                  <Plus size={14} className="mr-1.5" />
                  Agregar medio de pago
                </Boton>
              )}
            </section>

            <section data-redondeo className="border border-borde rounded-xl bg-superficie p-4">
              <h2 className="text-[14px] font-semibold">Redondeo</h2>
              <p className="text-[12.5px] text-tinta-2 mt-1 leading-snug">
                Cómo se deja el precio final. Redondear a miles se lee mejor, pero acerca los medios
                de pago entre sí: en productos baratos pueden quedar al mismo precio.
              </p>
              {puedeEditar ? (
                <select
                  aria-label="Redondeo"
                  value={reglas.redondeo ?? "miles"}
                  onChange={(e) => setBorrador((b) => b && ({ ...b, redondeo: e.target.value as "miles" | "ninguno" }))}
                  className="mt-2.5 h-9 px-2.5 text-[13px] bg-elevado border border-borde rounded-lg
                             cursor-pointer focus:outline-none focus:border-marca/50"
                >
                  {REDONDEOS.map((r) => <option key={r.valor} value={r.valor}>{r.etiqueta}</option>)}
                </select>
              ) : (
                <p data-redondeo-texto className="text-[13px] mt-2.5">
                  {REDONDEOS.find((r) => r.valor === (reglas.redondeo ?? "miles"))?.etiqueta ?? reglas.redondeo}
                </p>
              )}
            </section>

            {/* ⛔⛔ LA PREVISUALIZACIÓN. Sale de `cotizar`, siempre. */}
            <section data-previsualizacion className="border border-borde rounded-xl bg-superficie p-4">
              <h2 className="text-[14px] font-semibold">Lo que el bot va a decir</h2>
              <p className="text-[12.5px] text-tinta-2 mt-1 leading-snug">
                Calculado con la misma función que usa el bot al hablar con un cliente. Escribe el
                precio de lista de un producto y mira el resultado.
              </p>

              <label className="flex items-center gap-2 mt-2.5">
                <span className="text-[12.5px] text-tinta-2">Precio de lista</span>
                <input
                  aria-label="Precio de ejemplo"
                  type="number" min={0} step={1000}
                  value={precioEjemplo}
                  onChange={(e) => setPrecioEjemplo(Number(e.target.value))}
                  className="w-40 h-9 px-2.5 text-[13px] bg-elevado border border-borde rounded-lg
                             focus:outline-none focus:border-marca/50"
                />
              </label>

              {vista?.ok && (
                <ul data-cotizacion className="mt-3 divide-y divide-borde-suave">
                  {vista.medios.map((m, i) => (
                    <li key={i} data-cotiza={m.medio} className="flex items-center gap-3 py-1.5">
                      <span className="flex-1 min-w-0 text-[13px]">{m.medio}</span>
                      <span className="text-[12px] text-tinta-2">−{m.desc_pct}%</span>
                      <span data-precio className="text-[13px] font-semibold tabular-nums">
                        {formatearCOP(m.precio)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {vista && !vista.ok && (
                <div data-no-cotiza className="mt-3 rounded-lg border border-peligro/40 bg-peligro/8 p-3">
                  <p className="text-[13px] font-semibold">
                    Con este precio, el bot NO daría una cotización.
                  </p>
                  <p className="text-[12.5px] text-tinta-2 mt-1 leading-snug">{vista.motivo}</p>
                </div>
              )}
            </section>

            {/* El barrido: dónde deja de poder cotizar. */}
            <section data-barrido className="border border-borde rounded-xl bg-superficie p-4">
              <h2 className="text-[14px] font-semibold">Hasta dónde alcanza</h2>
              {colapsos.length === 0 ? (
                <p data-barrido-limpio className="text-[12.5px] text-tinta-2 mt-1 leading-snug">
                  De los {cuantosPrecios()} precios probados (de {formatearCOP(BARRIDO_DESDE)} a{" "}
                  {formatearCOP(BARRIDO_HASTA)}, de mil en mil), el bot puede cotizar todos.
                </p>
              ) : (
                <p data-barrido-roto className="text-[12.5px] text-tinta-2 mt-1 leading-snug">
                  De los {cuantosPrecios()} precios probados (de {formatearCOP(BARRIDO_DESDE)} a{" "}
                  {formatearCOP(BARRIDO_HASTA)}, de mil en mil), hay{" "}
                  <strong className="text-tinta">{colapsos.length}</strong> que el bot NO podría
                  cotizar: pasaría la conversación a un asesor. El más caro de ellos es{" "}
                  <strong className="text-tinta">{formatearCOP(colapsos[colapsos.length - 1]!)}</strong>.
                  Si el negocio no vende nada tan barato, no es un problema.
                </p>
              )}
            </section>

            {puedeEditar && (
              <div className="flex items-center justify-end gap-3 pt-1">
                <p className="text-[12px] text-tinta-2">
                  {hayCambios || hayCambiosSinFila
                    ? `El bot las toma en hasta ${MINUTOS_HASTA_QUE_EL_BOT_LO_TOMA} minutos.`
                    : "Sin cambios por guardar."}
                </p>
                <Boton tono="marca" onClick={alGuardar}
                  disabled={(!hayCambios && !hayCambiosSinFila) || ocupado}>
                  Guardar las reglas
                </Boton>
              </div>
            )}
          </>)}
        </div>
      </div>
    </div>
  );
}

/** Copia honda del `config`: sin esto, editar el borrador mutaría la fila
 *  cargada y `hayCambios` diría siempre «sin cambios». */
function clonar(r: ReglasPrecio): ReglasPrecio {
  return { ...r, medios_pago: r.medios_pago.map((m) => ({ ...m })) };
}
